import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import "./CSS/CustomerCreditPremium.css";
import "./CSS/MarginMasterPremium.css";
import {
  FaCheckCircle,
  FaEdit,
  FaExclamationCircle,
  FaLayerGroup,
  FaPlus,
  FaRedoAlt,
  FaSave,
  FaSearch,
  FaTimes,
  FaTrashAlt,
} from "react-icons/fa";

const pageSize = 5;

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === "success"
      ? FaCheckCircle
      : notice.type === "warning" || notice.type === "error"
      ? FaExclamationCircle
      : FaLayerGroup;

  return (
    <div
      className={`cc-toast ${notice.type}`}
      role={notice.type === "warning" || notice.type === "error" ? "alert" : "status"}
      aria-live={notice.type === "warning" || notice.type === "error" ? "assertive" : "polite"}
    >
      <div className="cc-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="cc-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="cc-toast-close" type="button" onClick={onClose} aria-label="Close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

function StatCard({ tone, icon: Icon, label, value, caption }) {
  return (
    <article className={`cc-summary-card ${tone}`}>
      <div className="cc-summary-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="cc-summary-copy">
        <span>{label}</span>
        <strong>{value}</strong>
        <em>{caption}</em>
      </div>
    </article>
  );
}

function ConfirmModal({ open, title, message, onCancel, onConfirm }) {
  if (!open) return null;

  return (
    <div className="margin-modal-overlay" role="presentation">
      <div className="margin-modal" role="dialog" aria-modal="true" aria-labelledby="margin-modal-title">
        <div className="margin-modal-head">
          <div className="margin-modal-icon">
            <FaTrashAlt aria-hidden="true" />
          </div>
          <div>
            <span>Confirm action</span>
            <h3 id="margin-modal-title">{title}</h3>
          </div>
        </div>
        <p>{message}</p>
        <div className="margin-modal-actions">
          <button type="button" className="btn btn-light" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function getPageNumbers(activePage, totalPages) {
  if (totalPages <= 1) return [1];
  const pages = new Set([1, totalPages, activePage, activePage - 1, activePage + 1]);
  return Array.from(pages)
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);
}

function money(value) {
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

const MarginMaster = () => {
  const token = localStorage.getItem("token");

  const [margins, setMargins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [fuelTypes, setFuelTypes] = useState([]);
  const [lubeNames, setLubeNames] = useState([]);

  const [form, setForm] = useState({
    id: null,
    product_type: "",
    unit: "LITRE",
    margin_basis: "PER_UNIT",
    margin_value: "",
    effective_from: "",
    effective_to: "",
    is_active: true,
  });

  const showNotice = (type, title, message) => {
    setNotice({ type, title, message });
    window.clearTimeout(showNotice._timer);
    showNotice._timer = window.setTimeout(() => setNotice(null), 3600);
  };

  const fetchMargins = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/margins", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMargins(res.data || []);
    } catch (err) {
      showNotice("error", "Load failed", err?.response?.data?.error || "Unable to load margins.");
    } finally {
      setLoading(false);
    }
  };

  const fetchFuelTypes = async () => {
    try {
      const res = await api.get("/api/nozzles/fuel-types", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFuelTypes(res.data || []);
    } catch (err) {
      showNotice("warning", "Fuel types unavailable", "Could not load fuel product types.");
    }
  };

  const fetchLubeNames = async () => {
    try {
      const res = await api.get("/api/lube-stock/product-names", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLubeNames(res.data || []);
    } catch (err) {
      showNotice("warning", "Lube names unavailable", "Could not load lube product names.");
    }
  };

  useEffect(() => {
    fetchMargins();
    fetchFuelTypes();
    fetchLubeNames();
  }, []);

  const resetForm = () => {
    setForm({
      id: null,
      product_type: "",
      unit: "LITRE",
      margin_basis: "PER_UNIT",
      margin_value: "",
      effective_from: "",
      effective_to: "",
      is_active: true,
    });
  };

  const filteredMargins = useMemo(() => {
    const q = search.trim().toLowerCase();
    return margins.filter((row) => {
      const matchesSearch =
        !q ||
        [row.product_type, row.unit, row.margin_basis, row.effective_from, row.effective_to]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && Number(row.is_active || 0) === 1) ||
        (statusFilter === "inactive" && Number(row.is_active || 0) !== 1);
      return matchesSearch && matchesStatus;
    });
  }, [margins, search, statusFilter]);

  const summary = useMemo(() => {
    const active = margins.filter((row) => Number(row.is_active || 0) === 1).length;
    const perUnit = margins.filter((row) => row.margin_basis === "PER_UNIT").length;
    const percentage = margins.filter((row) => row.margin_basis === "PERCENTAGE").length;
    const fuelCount = margins.filter((row) => fuelTypes.includes(row.product_type)).length;
    const lubeCount = margins.filter((row) => lubeNames.includes(row.product_type)).length;
    return { active, perUnit, percentage, fuelCount, lubeCount };
  }, [margins, fuelTypes, lubeNames]);

  const totalPages = Math.max(1, Math.ceil(filteredMargins.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const rows = filteredMargins.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const productOptions = useMemo(
    () => Array.from(new Set([...(fuelTypes || []), ...(lubeNames || [])])),
    [fuelTypes, lubeNames]
  );

  const handleProductTypeChange = (e) => {
    const value = e.target.value;
    let unit = form.unit;
    if (fuelTypes.includes(value)) unit = "LITRE";
    if (lubeNames.includes(value)) unit = "PIECE";
    setForm((prev) => ({ ...prev, product_type: value, unit }));
  };

  const handleEdit = (row) => {
    setForm({
      id: row.id,
      product_type: row.product_type,
      unit: row.unit || "LITRE",
      margin_basis: row.margin_basis,
      margin_value: row.margin_value,
      effective_from: row.effective_from ? row.effective_from.slice(0, 10) : "",
      effective_to: row.effective_to ? row.effective_to.slice(0, 10) : "",
      is_active: row.is_active === 1 || row.is_active === true,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = async () => {
    const marginValue = Number(form.margin_value || 0);

    if (!form.product_type) {
      showNotice("warning", "Product required", "Please choose a product type.");
      return;
    }
    if (!marginValue || marginValue <= 0) {
      showNotice("warning", "Margin value required", "Please enter a valid margin value.");
      return;
    }
    if (!form.effective_from) {
      showNotice("warning", "Effective from required", "Please choose the effective-from date.");
      return;
    }
    if (form.effective_to && form.effective_to < form.effective_from) {
      showNotice("warning", "Date range invalid", "Effective to cannot be earlier than effective from.");
      return;
    }

    const payload = {
      product_type: form.product_type,
      unit: form.unit,
      margin_basis: form.margin_basis,
      margin_value: marginValue,
      effective_from: form.effective_from,
      effective_to: form.effective_to || null,
      is_active: !!form.is_active,
    };

    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/api/margins/${form.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showNotice("success", "Margin updated", "The margin row has been saved.");
      } else {
        await api.post("/api/margins", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        showNotice("success", "Margin saved", "The new margin row is ready.");
      }
      resetForm();
      await fetchMargins();
    } catch (err) {
      showNotice("error", "Save failed", err?.response?.data?.error || "Unable to save margin.");
    } finally {
      setSaving(false);
    }
  };

  const deleteMargin = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/margins/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showNotice("success", "Margin deleted", "The margin row has been removed.");
      await fetchMargins();
    } catch (err) {
      showNotice("error", "Delete failed", err?.response?.data?.error || "Unable to delete margin.");
    } finally {
      setDeleteTarget(null);
    }
  };

  const activeLabel = form.margin_basis === "PER_UNIT" ? "₹ / Unit" : "% of Sale";

  return (
    <div className="cc-page text-light margin-page">
      <NotificationToast notice={notice} onClose={() => setNotice(null)} />
      <ConfirmModal
        open={!!deleteTarget}
        title="Delete margin row?"
        message="This will remove the selected margin row from the master list."
        onCancel={() => setDeleteTarget(null)}
        onConfirm={deleteMargin}
      />

      <section className="cc-hero">
        <div>
          <div className="cc-eyebrow">Margin Master</div>
          <h2>Margins that look and feel like the rest of the app.</h2>
          <p>
            Keep the pricing setup readable, mobile-friendly, and easy to maintain across fuel and lube
            products.
          </p>
          <div className="cc-hero-meta">
            <span>{margins.length} margin rows</span>
            <span>{summary.active} active</span>
            <span>{summary.perUnit} per-unit</span>
          </div>
        </div>

        <div className="cc-hero-actions">
          <button type="button" className="btn cc-secondary-action" onClick={fetchMargins} disabled={loading}>
            <FaRedoAlt aria-hidden="true" />
            Refresh
          </button>
          <button type="button" className="btn cc-primary-action" onClick={resetForm}>
            <FaPlus aria-hidden="true" />
            New margin
          </button>
        </div>
      </section>

      <section className="cc-summary-grid">
        <StatCard
          tone="blue"
          icon={FaLayerGroup}
          label="Rows"
          value={String(margins.length)}
          caption="All margin entries"
        />
        <StatCard
          tone="green"
          icon={FaCheckCircle}
          label="Active"
          value={String(summary.active)}
          caption="Currently usable rows"
        />
        <StatCard
          tone="amber"
          icon={FaEdit}
          label="Fuel / Lube"
          value={`${summary.fuelCount}/${summary.lubeCount}`}
          caption="Rows mapped to product groups"
        />
        <StatCard
          tone="slate"
          icon={FaLayerGroup}
          label="Basis mix"
          value={`${summary.perUnit}/${summary.percentage}`}
          caption={`Per-unit / ${activeLabel}`}
        />
      </section>

      <section className="cc-context-strip">
        <div className="cc-context-head">
          <div>
            <span>Margin context</span>
            <h3>Search and filter before opening the table</h3>
          </div>
          <div className="cc-context-chip">
            <strong>{statusFilter === "all" ? "All statuses" : statusFilter}</strong>
            <span>{filteredMargins.length} rows in view</span>
          </div>
        </div>

        <div className="margin-filter-grid">
          <div className="cc-search-shell">
            <FaSearch aria-hidden="true" />
            <input
              className="form-control"
              placeholder="Search product type, basis, or dates"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
        </div>
      </section>

      <section className="cc-grid margin-grid">
        <div className="cc-card cc-form-card">
          <div className="cc-card-head">
            <div>
              <span>Margin form</span>
              <h3>{form.id ? "Edit the margin row" : "Add a new margin row"}</h3>
            </div>
            <div className="cc-ledger-chip">
              <strong>{activeLabel}</strong>
              <span>Current basis</span>
            </div>
          </div>

          <div className="cc-form-grid margin-form-grid">
            <div>
              <label className="form-label">Product type</label>
              <select
                className="form-select"
                value={form.product_type}
                onChange={handleProductTypeChange}
              >
                <option value="">Select product</option>
                {fuelTypes.length > 0 && (
                  <optgroup label="Fuels">
                    {fuelTypes.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </optgroup>
                )}
                {lubeNames.length > 0 && (
                  <optgroup label="Lubes">
                    {lubeNames.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </optgroup>
                )}
                {!fuelTypes.length && !lubeNames.length && productOptions.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Unit</label>
              <select
                className="form-select"
                value={form.unit}
                onChange={(e) => setForm((prev) => ({ ...prev, unit: e.target.value }))}
              >
                <option value="LITRE">Litre</option>
                <option value="PIECE">Piece</option>
              </select>
            </div>
            <div>
              <label className="form-label">Margin basis</label>
              <select
                className="form-select"
                value={form.margin_basis}
                onChange={(e) => setForm((prev) => ({ ...prev, margin_basis: e.target.value }))}
              >
                <option value="PER_UNIT">₹ per Unit</option>
                <option value="PERCENTAGE">% of Sale Amount</option>
              </select>
            </div>
            <div>
              <label className="form-label">Margin value</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                value={form.margin_value}
                placeholder={form.margin_basis === "PER_UNIT" ? "₹ per unit" : "% of sale amount"}
                onChange={(e) => setForm((prev) => ({ ...prev, margin_value: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">Effective from</label>
              <input
                type="date"
                className="form-control"
                value={form.effective_from}
                onChange={(e) => setForm((prev) => ({ ...prev, effective_from: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">Effective to</label>
              <input
                type="date"
                className="form-control"
                value={form.effective_to}
                onChange={(e) => setForm((prev) => ({ ...prev, effective_to: e.target.value }))}
              />
            </div>
            <div className="margin-switch-row">
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                />
                <span>Active</span>
              </label>
            </div>
          </div>

          <div className="cc-action-row">
            <button type="button" className="btn cc-primary-action" onClick={handleSave} disabled={saving}>
              <FaSave aria-hidden="true" />
              {saving ? "Saving..." : form.id ? "Update margin" : "Save margin"}
            </button>
            {form.id && (
              <button type="button" className="btn btn-light" onClick={resetForm}>
                Cancel edit
              </button>
            )}
          </div>
        </div>

        <div className="cc-card cc-form-card">
          <div className="cc-card-head">
            <div>
              <span>Margin insights</span>
              <h3>Quick sense of the pricing setup</h3>
            </div>
          </div>

          <div className="margin-insight-stack">
            <div className="margin-insight-note">
              <strong>Per-unit vs percentage</strong>
              <span>{summary.perUnit} per-unit rows and {summary.percentage} percentage-based rows.</span>
            </div>
            <div className="margin-insight-note">
              <strong>Product mix</strong>
              <span>{summary.fuelCount} fuel rows and {summary.lubeCount} lube rows in the current list.</span>
            </div>
            <div className="margin-insight-note">
              <strong>Visibility</strong>
              <span>Headers and action cells now follow the same premium pattern as the other screens.</span>
            </div>
          </div>
        </div>
      </section>

      <section className="cc-card cc-report-card">
        <div className="cc-card-head">
          <div>
            <span>Margin register</span>
            <h3>Table header stays white and readable</h3>
          </div>
          <div className="cc-table-meta">
            <span>{filteredMargins.length} rows</span>
            <strong>
              {safePage}/{totalPages}
            </strong>
          </div>
        </div>

        <div className="table-responsive cc-table-wrap">
          <table className="table cc-table">
            <thead>
              <tr>
                <th style={{ color: "#fff", background: "#0f172a" }}>Product Type</th>
                <th style={{ color: "#fff", background: "#0f172a" }}>Unit</th>
                <th style={{ color: "#fff", background: "#0f172a" }}>Basis</th>
                <th style={{ color: "#fff", background: "#0f172a" }}>Value</th>
                <th style={{ color: "#fff", background: "#0f172a" }}>Effective From</th>
                <th style={{ color: "#fff", background: "#0f172a" }}>Effective To</th>
                <th style={{ color: "#fff", background: "#0f172a" }}>Active</th>
                <th style={{ width: 180, color: "#fff", background: "#0f172a" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8">
                    <div className="cc-empty-state">
                      <FaLayerGroup aria-hidden="true" />
                      <strong>Loading margins</strong>
                      <span>Please wait while the register is fetched.</span>
                    </div>
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Product Type">
                      <strong>{row.product_type}</strong>
                      <span>{fuelTypes.includes(row.product_type) ? "Fuel" : lubeNames.includes(row.product_type) ? "Lube" : "Product"}</span>
                    </td>
                    <td data-label="Unit">{row.unit}</td>
                    <td data-label="Basis">{row.margin_basis === "PER_UNIT" ? "₹ / Unit" : "% of Sale"}</td>
                    <td data-label="Value">{money(row.margin_value)}</td>
                    <td data-label="Effective From">{row.effective_from ? row.effective_from.slice(0, 10) : "-"}</td>
                    <td data-label="Effective To">{row.effective_to ? row.effective_to.slice(0, 10) : "-"}</td>
                    <td data-label="Active">
                      <span className={`cc-pill ${Number(row.is_active || 0) ? "paid" : "pending"}`}>
                        {Number(row.is_active || 0) ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td data-label="Actions" className="cc-row-actions margin-actions">
                      <button type="button" className="btn btn-sm btn-light cc-mini-btn" onClick={() => handleEdit(row)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger cc-mini-btn"
                        onClick={() => setDeleteTarget(row)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8">
                    <div className="cc-empty-state">
                      <FaLayerGroup aria-hidden="true" />
                      <strong>No margins defined</strong>
                      <span>Try another search or add the first margin row.</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredMargins.length > pageSize && (
          <div className="cc-pagination">
            <button
              type="button"
              className="cc-page-btn"
              disabled={safePage === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </button>
            <div className="cc-page-list">
              {getPageNumbers(safePage, totalPages).map((pageNo) => (
                <button
                  key={pageNo}
                  type="button"
                  className={`cc-page-btn ${safePage === pageNo ? "active" : ""}`}
                  onClick={() => setPage(pageNo)}
                >
                  {pageNo}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="cc-page-btn"
              disabled={safePage === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default MarginMaster;
